import { API_BASE_URL } from '@synergy/config';
import { Box, Chip, Stack, styled } from '@mui/material';
import { ConfirmationDialog, PoolButton, PoolFormattedMessage, PoolModal, PoolSelect } from '@synergy/components';
import { FormattedMessage } from 'react-intl';
import { PoolSelectOption, StyledMobileStepper } from '@synergy/components-next';
import { ReactElement, ReactNode, useEffect, useState } from 'react';
import { getLocalizedString } from '@synergy/utils';

import { Contact } from '../../common/models/contact';
import { ContactsLoginsApi } from './contacts-logins-api';
import { isLoginUser } from './models/login-user';
import { localesSelector } from '../../common/selectors/locale-selectors';
import { useSelector } from '../../store/use-appstate-selector';

const StyledChip = styled(Chip)(({ theme }) => ({
    fontSize: 12,
    fontWeight: 'bold',
    height: theme.spacing(4),
    padding: theme.spacing(),
    borderRadius: 18,
    margin: theme.spacing(0.5),

    '& svg': {
        color: 'rgba(255, 255, 255, 0.7)',
        margin: '0px 4px 0px 0px !important',
    },
    '& > span': {
        padding: '0 8px',
        textAlign: 'center',
    },
}));

export interface ContactsLoginsDeleteEmployeeModalBaseProps {
    isOpen: boolean;
    user?: Contact;
    title: ReactNode;
    message: ReactNode;
    onClose: () => void;
    onConfirm: (
        transferTagsId?: number,
        assignMainContactToContactId?: number,
        assignBonusContactToContactId?: number
    ) => void;
}

const api = new ContactsLoginsApi(API_BASE_URL);

enum StepType {
    MainContact,
    Topics,
    BonusContact,
}

interface Step {
    type: StepType;
    content: ReactNode;
}

export type ContactsLoginsDeleteEmployeeModalProps = Omit<
    ContactsLoginsDeleteEmployeeModalBaseProps,
    'title' | 'message'
>;

export const ContactsLoginsDeleteEmployeeModal = (props: ContactsLoginsDeleteEmployeeModalProps) => {
    return (
        <ContactsLoginsDeleteEmployeeModalBase
            {...props}
            title={<FormattedMessage id="app.contacts.logins.delete.employee" />}
            message={
                <PoolFormattedMessage
                    id="app.contacts.logins.delete.employee.message"
                    values={{ value: props.user?.fullName }}
                />
            }
        />
    );
};

export const ContactsLoginsDeleteLoginModal = (props: ContactsLoginsDeleteEmployeeModalProps) => {
    return (
        <ContactsLoginsDeleteEmployeeModalBase
            {...props}
            title={<FormattedMessage id="app.contacts.logins.delete.user" />}
            message={
                <PoolFormattedMessage
                    id="app.contacts.logins.delete.user.message"
                    values={{ value: props.user?.fullName }}
                />
            }
        />
    );
};

const ContactsLoginsDeleteEmployeeModalBase = (
    props: ContactsLoginsDeleteEmployeeModalBaseProps
): ReactElement | null => {
    const { isOpen, user, title, message, onClose, onConfirm } = props;
    const [topicOptions, setTopicOptions] = useState<PoolSelectOption<number>[]>([]);
    const [mainContactOptions, setMainContactOptions] = useState<PoolSelectOption<number>[]>([]);
    const [bonusContactOptions, setBonusContactOptions] = useState<PoolSelectOption<number>[]>([]);
    const [selectedTagsEmployeeId, setSelectedTagsEmployeeId] = useState<number>();
    const [selectedMainEmployeeId, setSelectedMainEmployeeId] = useState<number>();
    const [selectedBonusContactId, setSelectedBonusContactId] = useState<number>();
    const [steps, setSteps] = useState<Step[]>([]);
    const [activeStep, setActiveStep] = useState(0);
    const locales = useSelector(localesSelector);
    const bonusContactsCount = useSelector(state => state.contactsLogins.bonusContactCount);

    useEffect(() => {
        if (!isOpen || !user) return;

        const fetchContacts = async () => {
            const params = new URLSearchParams({ limit: '1000' });
            const response = await api.cget(params);
            if (response.success) {
                const topicOptions = response.result.items
                    .filter(contact => contact.id !== user.id && isLoginUser(contact) && contact.user.isActivated)
                    .map<PoolSelectOption<number>>(contact => ({
                        value: contact.id,
                        displayValue: contact.fullName,
                    }));
                const mainContactOptions = response.result.items
                    .filter(contact => contact.id !== user.id)
                    .map<PoolSelectOption<number>>(contact => ({
                        value: contact.id,
                        displayValue: contact.fullName,
                    }));
                const bonusContactOptions = response.result.items
                    .filter(contact => contact.id !== user.id)
                    .map<PoolSelectOption<number>>(contact => ({
                        value: contact.id,
                        displayValue: contact.fullName,
                    }));
                setTopicOptions(topicOptions);
                setMainContactOptions(mainContactOptions);
                setBonusContactOptions(bonusContactOptions);
            } else {
                setTopicOptions([]);
                setMainContactOptions([]);
                setBonusContactOptions([]);
            }
        };
        fetchContacts();
    }, [isOpen, user]);

    useEffect(() => {
        if (!isOpen || !user) return;
        const newSteps = [];
        if (user.topics && user.topics.length > 0) {
            newSteps.push({
                type: StepType.Topics,
                content: (
                    <>
                        <Box sx={{ mb: 0.5 }}>
                            <FormattedMessage
                                id="app.contacts.logins.delete.employee.topics"
                                values={{ value: user.fullName }}
                            />
                        </Box>
                        <Box sx={{ mb: 5 }}>
                            {user.topics?.map(topic => {
                                return (
                                    <StyledChip
                                        key={topic.id}
                                        color="primary"
                                        label={getLocalizedString(locales, topic.title)}
                                    />
                                );
                            })}
                        </Box>
                        <Box sx={{ mb: 3 }}>
                            <FormattedMessage id="app.contacts.logins.delete.employee.topicsHint" />
                        </Box>
                        <PoolSelect
                            options={topicOptions}
                            fullWidth={true}
                            value={selectedTagsEmployeeId ?? ''}
                            onValueChange={setSelectedTagsEmployeeId}
                            label={<FormattedMessage id="app.contacts.logins.delete.employee.select" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }
        if (user.isMainContact) {
            newSteps.push({
                type: StepType.MainContact,
                content: (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <PoolFormattedMessage
                                id="app.contacts.logins.delete.employee.mainContact"
                                values={{ value: user.fullName }}
                            />
                        </Box>
                        <PoolSelect
                            options={mainContactOptions}
                            fullWidth={true}
                            value={selectedMainEmployeeId ?? ''}
                            onValueChange={setSelectedMainEmployeeId}
                            label={<FormattedMessage id="app.contacts.logins.delete.employee.mainContactSelect" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }
        if (user.isBonusContact && bonusContactsCount === 1) {
            newSteps.push({
                type: StepType.BonusContact,
                content: (
                    <>
                        <Box sx={{ mb: 3 }}>
                            <PoolFormattedMessage
                                id="app.contacts.logins.delete.employee.bonusContact"
                                values={{ value: user.fullName }}
                            />
                        </Box>
                        <PoolSelect
                            options={bonusContactOptions}
                            fullWidth={true}
                            value={selectedBonusContactId ?? ''}
                            onValueChange={setSelectedBonusContactId}
                            label={<FormattedMessage id="app.contacts.logins.delete.employee.bonusContactSelect" />}
                            disallowDefaultValue={true}
                        />
                    </>
                ),
            });
        }
        setSteps(newSteps);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        user,
        topicOptions,
        mainContactOptions,
        setSelectedTagsEmployeeId,
        setSelectedMainEmployeeId,
        setSelectedBonusContactId,
    ]);

    if (!user) {
        return null;
    }

    const disabledStep =
        (steps[activeStep]?.type === StepType.MainContact && !selectedMainEmployeeId) ||
        (steps[activeStep]?.type === StepType.Topics && !selectedTagsEmployeeId) ||
        (steps[activeStep]?.type === StepType.BonusContact && !selectedBonusContactId);

    if (steps.length === 0) {
        return (
            <ConfirmationDialog
                title={title}
                action={<FormattedMessage id="app.common.delete" />}
                isVisible={isOpen}
                onCancel={onClose}
                onConfirm={() => onConfirm()}
            >
                {message}
            </ConfirmationDialog>
        );
    }

    if (steps.length === 1) {
        return (
            <PoolModal
                title={title}
                isVisible={isOpen}
                handleClose={onClose}
                actionChildren={[
                    <span key="1" />,
                    <PoolButton
                        key="2"
                        color="secondary"
                        styleType="firstLevel"
                        disabled={disabledStep}
                        onClick={() =>
                            onConfirm(selectedTagsEmployeeId, selectedMainEmployeeId, selectedBonusContactId)
                        }
                    >
                        <FormattedMessage id="app.common.delete" />
                    </PoolButton>,
                ]}
            >
                <Stack sx={{ textAlign: 'center' }}>{steps[0]?.content}</Stack>
            </PoolModal>
        );
    }

    return (
        <PoolModal
            title={<FormattedMessage id="app.contacts.logins.delete.employee" />}
            isVisible={isOpen}
            handleClose={onClose}
            actionChildren={
                <StyledMobileStepper
                    activeStep={activeStep}
                    steps={steps.length}
                    variant="dots"
                    backButton={
                        <PoolButton
                            variant="text"
                            color="primary"
                            styleType="firstLevel"
                            disabled={activeStep === 0}
                            onClick={() => setActiveStep(prevActiveStep => prevActiveStep - 1)}
                        >
                            <FormattedMessage id="app.common.back" />
                        </PoolButton>
                    }
                    nextButton={
                        activeStep === steps.length - 1 ? (
                            <PoolButton
                                color="secondary"
                                styleType="firstLevel"
                                disabled={disabledStep}
                                onClick={() =>
                                    onConfirm(selectedTagsEmployeeId, selectedMainEmployeeId, selectedBonusContactId)
                                }
                            >
                                <FormattedMessage id="app.common.delete" />
                            </PoolButton>
                        ) : (
                            <PoolButton
                                color="primary"
                                styleType="firstLevel"
                                disabled={disabledStep}
                                onClick={() => setActiveStep(prevActiveStep => prevActiveStep + 1)}
                            >
                                <FormattedMessage id="app.common.next" />
                            </PoolButton>
                        )
                    }
                />
            }
            sx={{ content: { alignContent: 'center', minHeight: 270 } }}
        >
            <Stack sx={{ textAlign: 'center' }}>{steps[activeStep]?.content}</Stack>
        </PoolModal>
    );
};
